import { useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Copy, Check, ExternalLink, FileText, Mail, Loader2, ClipboardList } from "lucide-react";

function CopyRow({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      toast.error("Couldn't copy — copy manually");
    }
  };
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-zinc-200 bg-white px-3 py-2">
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="text-sm text-zinc-800 break-words whitespace-pre-wrap mt-0.5">{value}</p>
      </div>
      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-[#24468C]" onClick={copy} data-testid={`copy-${testid}`} title={`Copy ${label}`}>
        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" strokeWidth={1.5} />}
      </Button>
    </div>
  );
}

function fmtExperience(w) {
  const period = w.end_date ? `${w.start_date || ""} – ${w.end_date}` : (w.start_date ? `${w.start_date} – Present` : "");
  const header = `${w.role || ""}${w.company ? ` — ${w.company}` : ""}${period ? ` (${period})` : ""}`.trim();
  const bullets = (w.description || "")
    .split(/\r?\n|(?<=[.!?])\s+(?=[A-Z0-9])/)
    .map((s) => s.replace(/^[•\-\u2022*]\s*/, "").trim())
    .filter(Boolean)
    .map((s) => `\u2022 ${s}`);
  return [header, ...bullets].filter(Boolean).join("\n");
}

function ProfileCopyList({ profile, work, skills }) {
  const allText = () => {
    const p = profile || {};
    const lines = [];
    if (p.full_name) lines.push(p.full_name);
    const contact = [p.email, p.phone, p.location].filter(Boolean).join("  |  ");
    if (contact) lines.push(contact);
    if (p.linkedin_url) lines.push(p.linkedin_url);
    if (p.portfolio_url) lines.push(p.portfolio_url);
    if (p.summary) lines.push("", "PROFESSIONAL SUMMARY", p.summary);
    if (skills.length) lines.push("", "SKILLS", skills.map((s) => s.name).join(", "));
    if (work.length) {
      lines.push("", "WORK EXPERIENCE");
      work.forEach((w) => { lines.push("", fmtExperience(w)); });
    }
    return lines.join("\n").trim();
  };
  const copyAll = async () => {
    try { await navigator.clipboard.writeText(allText()); toast.success("All details copied — paste straight into the form"); }
    catch (_) { toast.error("Couldn't copy — copy manually"); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">One-click copy</p>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={copyAll} data-testid="copy-all-btn">
          <Copy className="h-3.5 w-3.5 mr-1" /> Copy all
        </Button>
      </div>
      <div className="space-y-2">
        <CopyRow label="Full name" value={profile?.full_name} testid="name" />
        <CopyRow label="Email" value={profile?.email} testid="email" />
        <CopyRow label="Phone" value={profile?.phone} testid="phone" />
        <CopyRow label="Location" value={profile?.location} testid="location" />
        <CopyRow label="Professional summary" value={profile?.summary} testid="summary" />
        <CopyRow label="Skills" value={skills.map((s) => s.name).join(", ")} testid="skills" />
        {work.map((w) => (
          <CopyRow key={w.id} label={`Experience · ${w.role} @ ${w.company}`} value={fmtExperience(w)} testid={`work-${w.id}`} />
        ))}
      </div>
    </div>
  );
}

function DocumentsSection({ cvs, cover, selectedCv, setSelectedCv, dl }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Your documents</p>
      {cvs.length > 0 ? (
        <>
          <Select value={selectedCv || "none"} onValueChange={(v) => setSelectedCv(v === "none" ? "" : v)}>
            <SelectTrigger data-testid="apply-cv-select" className="mb-2"><SelectValue placeholder="Choose a tailored CV" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Select a tailored CV…</SelectItem>
              {cvs.map((t) => <SelectItem key={t.id} value={t.id}>{t.job_title} · {t.company}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" disabled={!selectedCv} onClick={() => dl(`/tailored-cvs/${selectedCv}/download`)} data-testid="apply-download-cv-btn">
              <FileText className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> CV (PDF)
            </Button>
            <Button variant="outline" disabled={!cover} onClick={() => dl(`/cover-letters/${cover.id}/download`)} data-testid="apply-download-cover-btn">
              <Mail className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Cover letter
            </Button>
          </div>
          {selectedCv && !cover && <p className="text-xs text-muted-foreground mt-1.5">No cover letter saved for this CV.</p>}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">No tailored CVs yet. Tailor one from the Tailor CV page to download here.</p>
      )}
    </div>
  );
}

function detectAts(url) {
  const u = (url || "").toLowerCase();
  if (u.includes("greenhouse.io") || u.includes("grnh.se")) return "greenhouse";
  if (u.includes("myworkdayjobs.com") || u.includes(".workday")) return "workday";
  if (u.includes("lever.co")) return "lever";
  return null;
}

const ATS_LABEL = { greenhouse: "Greenhouse", workday: "Workday", lever: "Lever" };

function splitName(full) {
  const parts = (full || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return { first: full || "", last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

function atsFields(ats, profile) {
  const p = profile || {};
  const { first, last } = splitName(p.full_name);
  const F = {
    first_name: { label: "First name", value: first },
    last_name: { label: "Last name", value: last },
    full_name: { label: "Full name", value: p.full_name },
    email: { label: "Email", value: p.email },
    phone: { label: "Phone", value: p.phone },
    location: { label: "Location", value: p.location },
    linkedin: { label: "LinkedIn URL", value: p.linkedin_url },
    portfolio: { label: "Website / Portfolio", value: p.portfolio_url },
    work_auth: { label: "Work authorization", value: p.work_authorization },
    sponsorship: { label: "Requires sponsorship?", value: p.requires_sponsorship },
    notice: { label: "Notice period", value: p.notice_period },
    salary: { label: "Desired salary", value: p.desired_salary },
  };
  const order = {
    greenhouse: ["first_name", "last_name", "email", "phone", "location", "linkedin", "portfolio", "work_auth", "sponsorship"],
    workday: ["first_name", "last_name", "email", "phone", "location", "work_auth", "sponsorship", "notice", "salary", "linkedin"],
    lever: ["full_name", "email", "phone", "location", "linkedin", "portfolio", "salary"],
  }[ats] || [];
  return order.map((k) => ({ ...F[k], key: k })).filter((f) => f.value);
}

function AtsFieldList({ ats, profile }) {
  const fields = atsFields(ats, profile);
  return (
    <div data-testid="ats-field-list">
      <div className="flex items-center gap-2 mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fields for {ATS_LABEL[ats]}</p>
        <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#24468C]/10 text-[#24468C] px-1.5 py-0.5 rounded" data-testid="ats-detected-badge">Detected</span>
      </div>
      {fields.length === 0 ? (
        <p className="text-xs text-muted-foreground">Add your details on the Profile page to copy them here.</p>
      ) : (
        <div className="space-y-2">
          {fields.map((f) => <CopyRow key={f.key} label={f.label} value={f.value} testid={`ats-${f.key}`} />)}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-2">Copy these into the {ATS_LABEL[ats]} form in order. Add work history, education & screening answers from the sections below.</p>
    </div>
  );
}

export function ApplyAssistPanel({ open, onOpenChange, job, onApplied }) {
  const [profile, setProfile] = useState(null);
  const [work, setWork] = useState([]);
  const [skills, setSkills] = useState([]);
  const [cvs, setCvs] = useState([]);
  const [covers, setCovers] = useState([]);
  const [selectedCv, setSelectedCv] = useState("");
  const [loading, setLoading] = useState(true);
  const [applied, setApplied] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open || !job) return;
    setApplied(job.status === "applied");
    setLoading(true);
    (async () => {
      try {
        const [p, t, c] = await Promise.all([
          api.get("/profile"),
          api.get("/tailored-cvs"),
          api.get("/cover-letters"),
        ]);
        setProfile(p.data.profile || {});
        setWork(p.data.work_experiences || []);
        setSkills(p.data.skills || []);
        setCvs(t.data || []);
        setCovers(c.data || []);
        // Auto-match a tailored CV by company/role, fall back to newest.
        const norm = (s) => (s || "").toLowerCase().trim();
        const match = (t.data || []).find(
          (x) => (norm(x.company) && norm(x.company) === norm(job.company)) ||
                 (norm(x.job_title) && norm(x.job_title) === norm(job.job_title))
        );
        setSelectedCv(job.tailored_cv_id || (match && match.id) || "");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load your details.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, job]);

  const cover = covers.find((c) => c.tailored_cv_id === selectedCv);
  const ats = detectAts(job?.job_url);
  const dl = (path) => window.open(`${API}${path}`, "_blank", "noopener,noreferrer");
  const openJob = () => {
    if (job?.job_url) window.open(job.job_url, "_blank", "noopener,noreferrer");
    else toast.info("No job link saved for this application.");
  };

  const confirmApplied = async (checked) => {
    if (!checked || !job?.id) { setApplied(checked); return; }
    setConfirming(true);
    try {
      const payload = {
        company: job.company, job_title: job.job_title, job_url: job.job_url || null,
        job_description: job.job_description || null, status: "applied",
        date_applied: new Date().toISOString().slice(0, 10),
        tailored_cv_id: selectedCv || null,
      };
      await api.put(`/applications/${job.id}`, payload);
      setApplied(true);
      toast.success("Marked as applied 🎉");
      onApplied && onApplied();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update status.");
    } finally { setConfirming(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0" data-testid="apply-assist-panel">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-zinc-100">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-[#24468C]/10 flex items-center justify-center">
              <ClipboardList className="h-4 w-4 text-[#24468C]" strokeWidth={1.75} />
            </div>
            <SheetTitle className="font-heading text-lg">Quick-Apply helper</SheetTitle>
          </div>
          <SheetDescription className="text-left">
            <span className="font-medium text-zinc-800">{job?.job_title}</span>
            {job?.company ? ` · ${job.company}` : ""}
          </SheetDescription>
          <Button onClick={openJob} className="mt-2 w-full bg-[#24468C] hover:bg-[#1d3870]" data-testid="apply-open-job-btn">
            Open job in new tab <ExternalLink className="h-4 w-4 ml-1.5" />
          </Button>
        </SheetHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {ats && <AtsFieldList ats={ats} profile={profile} />}
            {ats && <Separator />}
            <ProfileCopyList profile={profile} work={work} skills={skills} />

            <Separator />

            <DocumentsSection cvs={cvs} cover={cover} selectedCv={selectedCv} setSelectedCv={setSelectedCv} dl={dl} />
          </div>
        )}

        <SheetFooter className="px-5 py-4 border-t border-zinc-100">
          <label className="flex items-center gap-3 w-full cursor-pointer rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-3">
            {confirming
              ? <Loader2 className="h-5 w-5 animate-spin text-[#24468C]" />
              : <Checkbox checked={applied} onCheckedChange={confirmApplied} disabled={applied || !job?.id} data-testid="apply-confirm-checkbox" />}
            <span className="text-sm font-medium text-zinc-800">
              {applied ? "Applied — nice work! 🎉" : "I've applied to this job"}
            </span>
          </label>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
